import type { SupabaseClient } from "@supabase/supabase-js"

/**
 * Returns a workspace name for `userId` that is not already used:
 * `desired`, then `desired (2)`, `desired (3)`, …
 */
export async function ensureUniqueWorkspaceName(
  client: SupabaseClient,
  userId: string,
  desired: string
): Promise<string> {
  const base = desired.trim() || "Workspace"
  for (let n = 0; n < 200; n++) {
    const candidate = n === 0 ? base : `${base} (${n + 1})`

    const { data, error } = await client
      .from("workspaces")
      .select("id")
      .eq("user_id", userId)
      .eq("name", candidate)
      .maybeSingle()

    if (error) throw error
    if (data == null) return candidate
  }
  return `${base} (${Date.now()})`
}

/**
 * @returns `null` if the exact name is free, else the existing workspace `id`.
 */
export async function findWorkspaceNameConflict(
  client: SupabaseClient,
  userId: string,
  name: string
): Promise<string | null> {
  const trimmed = name.trim()
  if (!trimmed) return null
  const { data, error } = await client
    .from("workspaces")
    .select("id")
    .eq("user_id", userId)
    .eq("name", trimmed)
    .maybeSingle()
  if (error) throw error
  return (data?.id as string | undefined) ?? null
}
